import axios from "axios";
import { API_CONSTANT_MAP } from "./paths";
import { authBus } from "./authBus";
import { CHAVES, SESSAO, storage } from "./sessionConfig";

/**
 * Renovação do token da sessão (POST /Login/renew).
 *
 * Deliberadamente NÃO importa o requestApi e NÃO passa pelos interceptors de
 * `api`: a renovação é silenciosa (não deve acender o overlay de loading
 * global) e não pode se auto-retentar — senão um 401 no próprio renew viraria
 * recursão. Também é isto que evita o ciclo requestApi -> renovacaoToken.
 */

export type ResultadoRenovacao =
  /** Token novo já gravado no localStorage. */
  | { status: "ok"; user: any }
  /** 401/403: a sessão morreu, não adianta tentar de novo. */
  | { status: "invalida" }
  /** 404: API anterior ao endpoint de renovação. Desliga a renovação proativa. */
  | { status: "indisponivel" }
  /** Rede/5xx: transitório, o próximo tick tenta novamente. */
  | { status: "falha" };

/** Promise em curso — três requisições com 401 simultâneo compartilham um renew. */
let emCurso: Promise<ResultadoRenovacao> | null = null;

export const temTokenArmazenado = (): boolean =>
  Boolean(storage.lerJson(CHAVES.USER)?.token);

/**
 * Lock entre abas. Não é garantia de exclusão mútua (nem precisa ser: sendo
 * stateless, dois renews concorrentes só produzem dois tokens válidos) —
 * serve para reduzir chamadas redundantes quando há várias abas abertas.
 */
const tentarObterLock = (): boolean => {
  const lock = Number(storage.ler(CHAVES.RENOVACAO_LOCK));
  if (lock && Date.now() - lock < SESSAO.RENOVACAO_LOCK_MS) return false;
  storage.gravar(CHAVES.RENOVACAO_LOCK, String(Date.now()));
  return true;
};

const liberarLock = () => storage.remover(CHAVES.RENOVACAO_LOCK);

const executar = async (): Promise<ResultadoRenovacao> => {
  const userAtual = storage.lerJson(CHAVES.USER);
  if (!userAtual?.token) return { status: "invalida" };

  try {
    // Instância criada a cada chamada (mesmo padrão do antigo checkTokenValidity):
    // é o que faz o adapter default do axios-mock-adapter interceptá-la nos testes.
    const response = await axios
      .create({
        baseURL: API_CONSTANT_MAP.baseUrl,
        headers: { "Content-Type": "application/json" },
      })
      .post(API_CONSTANT_MAP.loginRenew, null, {
        headers: { Authorization: "Bearer " + userAtual.token },
      });

    if (response.status !== 200 || !response.data?.token) {
      return { status: "falha" };
    }

    storage.gravar(CHAVES.USER, JSON.stringify(response.data));
    authBus.notificarSessaoRenovada(response.data);
    return { status: "ok", user: response.data };
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 401 || status === 403) return { status: "invalida" };
    if (status === 404) return { status: "indisponivel" };
    return { status: "falha" };
  }
};

export const renovarToken = (): Promise<ResultadoRenovacao> => {
  if (emCurso) return emCurso;

  emCurso = executar().finally(() => {
    emCurso = null;
    liberarLock();
  });
  return emCurso;
};

/**
 * Renovação proativa (agendada pelo sessionManager). Respeita o lock entre
 * abas; se outra aba já está renovando, esta apenas espera o próximo tick — o
 * token novo chega pelo evento de `storage`.
 */
export const renovarTokenSePossivel = (): Promise<ResultadoRenovacao> => {
  if (emCurso) return emCurso;
  if (!tentarObterLock()) return Promise.resolve({ status: "falha" });
  return renovarToken();
};

/** Somente para testes: descarta a promise compartilhada entre casos. */
export const resetarRenovacao = () => {
  emCurso = null;
  liberarLock();
};
